<template>
  <b-overlay
    :show="loading"
    rounded="sm"
    variant="primary"
    :opacity="0.2"
    style="min-height: 200px"
  >
    <b-card
      no-body
      class="border-0 mb-4 mt-3"
      v-show="professionsList.length">
      <div style="margin-bottom: 32px" v-if="isEnableCommonNames">
        <b-card
          no-body
          class="bg-white ai-custom-box"
        >
          <b-card-body class="px-3 py-3">
            <div class="text-dark mb-2 font-weight-bold" style="font-size: 15px;">
              {{ translations[15938] }}
            </div>

            <div class="d-flex flex-column" style="row-gap: 8px;">
              <label class="d-flex align-items-center m-0" style="column-gap: 12px; cursor: pointer;">
                <input
                  v-model="qLevel1"
                  type="checkbox"
                  class="ai-qlevel-checkbox"
                  style="accent-color: #e5e7eb;"
                />
                <div class="d-flex align-items-center w-100" style="column-gap: 12px;">
                  <qualification-level-bars :level="1" />
                  <span class="text-dark" style="font-size: 14px;">{{ translations[15935] }}</span>
                </div>
              </label>

              <label class="d-flex align-items-center m-0" style="column-gap: 12px; cursor: pointer;">
                <input
                  v-model="qLevel2"
                  type="checkbox"
                  class="ai-qlevel-checkbox"
                  style="accent-color: #9ca3af;"
                />
                <div class="d-flex align-items-center w-100" style="column-gap: 12px;">
                  <qualification-level-bars :level="2" />
                  <span class="text-dark" style="font-size: 14px;">{{ translations[15936] }}</span>
                </div>
              </label>

              <label class="d-flex align-items-center m-0" style="column-gap: 12px; cursor: pointer;">
                <input
                  v-model="qLevel3"
                  type="checkbox"
                  class="ai-qlevel-checkbox"
                  style="accent-color: #4b5563;"
                />
                <div class="d-flex align-items-center w-100" style="column-gap: 12px;">
                  <qualification-level-bars :level="3" />
                  <span class="text-dark" style="font-size: 14px;">{{ translations[15937] }}</span>
                </div>
              </label>
            </div>
          </b-card-body>
        </b-card>
      </div>
      <b-card-body class="px-2 py-0">
        <h3 class="mb-0">
          {{ jobsTitle }}
        </h3>
      </b-card-body>
      <professions-list
        :is-auth="isAuth"
        :language-sid="languageSid"
        :items="professionsListFiltered"
        :show-rank-bars="isEnableCommonNames"
      />
    </b-card>
  </b-overlay>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { ProfessionModule } from '@/store/modules/profession';
import ProfessionsList from '@/components/Home/ProfessionsList.vue';
import QualificationLevelBars from '@/components/Home/QualificationLevelBars.vue';
import { UserModule } from '@/store/modules/user';
import { ClustersModule } from '@/store/modules/clusters';
import { mixins } from 'vue-class-component';
import LocaleStringMixin from '@/mixins/LocaleStringMixin';

@Component({
  components: {
    ProfessionsList,
    QualificationLevelBars,
  },
})
export default class ProfessionsComponent extends mixins(LocaleStringMixin) {
  @Prop({ default: false }) private isCluster!: boolean;

  get translations() {
    return AppModule.translation;
  }

  loading = false;

  qLevel1 = true;

  qLevel2 = true;

  qLevel3 = true;

  get isEnableCommonNames() {
    return Number(AppModule.setting?.enable_common_names) === 1;
  }

  get professions() {
    return ProfessionModule.professions;
  }

  get lang() {
    return AppModule.language;
  }

  get selectProfession() {
    return ProfessionModule.searchJobProfession;
  }

  get selectLocation() {
    return ProfessionModule.searchJobLocation;
  }

  get professionTitle() {
    return ProfessionModule.professionTitle || ClustersModule.professionTitle;
  }

  get jobsTitle() {
    const count = this.isEnableCommonNames ? this.filteredJobsCount : this.jobsCount;
    if (this.selectProfession || this.selectLocation) {
      return `${this.toLocaleLangString(count || 0)} ${count === 1 ? this.translations[13772] : this.translations[13773]} ${this.selectProfession?.name || ''}${
        (this.selectProfession?.name && this.selectLocation?.name) ? ',' : ''
      } ${this.selectLocation?.name || ''}`;
    }
    return `${this.toLocaleLangString(count || 0)} ${count === 1 ? this.translations[13785] : this.translations[13774]}${this.isCluster ? `: ${this.professionTitle}` : ''}`;
  }

  get professionsList() {
    return this.professions.professions?.flat().sort((a: any, b: any) => b.amt - a.amt) || [];
  }

  get professionsListFiltered() {
    if (!this.isEnableCommonNames) return this.professionsList;

    const enabledRanks: number[] = [];
    if (this.qLevel1) enabledRanks.push(1);
    if (this.qLevel2) enabledRanks.push(2);
    if (this.qLevel3) enabledRanks.push(3);

    if (!enabledRanks.length) return [];

    return this.professionsList.filter((job: any) => enabledRanks.includes(Number(job?.rank)));
  }

  get filteredJobsCount() {
    return this.professionsListFiltered.reduce((sum: number, job: any) => sum + Number(job?.amt || 0), 0);
  }

  get allCount(): number {
    return Number(AppModule.setting?.all_count || 0);
  }

  get clustersCount(): any {
    const id = ClustersModule.jobClusterParams.cluster_id;
    return AppModule.jobCountPortal.clusters.find((x: any) => x.id === id);
  }

  get jobsCount() {
    if (this.isCluster) {
      return this.clustersCount.count || 0;
    }
    return this.allCount;
  }

  get languageSid() {
    return AppModule.languageSid;
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  async loadProfessionItems() {
    this.loading = true;
    const params = {
      sid: this.languageSid,
      lang: this.lang,
      cluster_id: ClustersModule.jobClusterParams.cluster_id,
      ...(this.selectProfession && { profession_id: this.selectProfession.id }),
    };
    if (!this.isCluster) {
      delete params.cluster_id;
    }
    await ProfessionModule.GetProfession(params);
    this.loading = false;
  }

  created() {
    this.loadProfessionItems();
  }
}
</script>

<style scoped>
/* Чекбокси Qualifikationsniveau як у макеті */
.ai-qlevel-checkbox {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  cursor: pointer;
}

.ai-custom-box {
  border: 1px solid #e5e7eb !important;
  border-radius: 8px;
}
</style>
